import {
  ButtonHTMLAttributes,
  forwardRef,
  useMemo,
  type AnchorHTMLAttributes,
} from 'react';
import { cva } from 'class-variance-authority';
import { tlwTwMerge, type GenerateCvaType } from '@trg/utils';
import { Icon, type IconProps } from '../icon';

export type IconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    htmlType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
    icon: IconProps['name'] | null;
    type?: 'default' | 'primary' | 'secondary' | 'tertiary';
    size?: 'tiny' | 'small' | 'medium' | 'large';
    iconSize?: IconProps['size'];
    outline?: boolean;
    square?: boolean;
  };

type IconButtonVariantsType = GenerateCvaType<
  IconButtonProps,
  'type' | 'size' | 'outline' | 'square'
>;

const iconButtonVariants = cva<IconButtonVariantsType>(
  [
    'tlw-transition tlw-duration-300 tlw-select-none',
    'disabled:tlw-cursor-not-allowed',
  ],
  {
    variants: {
      type: {
        default: [
          'tlw-text-txt-700',
          'hover:tlw-text-txt-core',
          'active:tlw-text-txt-core',
          'disabled:tlw-text-txt-500',
        ],
        primary: [
          'tlw-text-txt-700 tlw-bg-white',
          'hover:tlw-text-txt-white hover:tlw-bg-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr',
          'disabled:tlw-text-txt-white disabled:tlw-bg-gray-400',
          'tlw-shadow-[0px_0px_8px_0px_#0000001A]',
        ],
        secondary: [
          'tlw-text-txt-white tlw-bg-gray-800',
          'hover:tlw-text-txt-white hover:tlw-bg-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr',
          'disabled:tlw-text-txt-white disabled:tlw-bg-gray-400',
          'tlw-shadow-[0px_4px_12px_0px_#00000033]',
        ],
        tertiary: [
          'tlw-text-txt-white tlw-bg-gray-700',
          'hover:tlw-text-txt-white hover:tlw-bg-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr',
          'disabled:tlw-text-txt-white disabled:tlw-bg-gray-400',
        ],
      },
      size: {
        tiny: 'tlw-p-2 tlw-text-md',
        small: 'tlw-p-1.5 tlw-text-md md:tlw-p-2 md:tlw-text-xl',
        medium: 'tlw-p-2.5 tlw-text-xl',
        large: 'tlw-p-2.5 tlw-text-xl md:tlw-p-3 md:tlw-text-2xl',
      },
      outline: {
        true: '',
        false: '',
      },
      square: {
        true: 'tlw-rounded',
        false: 'tlw-rounded-full',
      },
    },
    compoundVariants: [
      {
        type: 'default',
        outline: true,
        class: [
          'tlw-border tlw-shadow-none',
          'tlw-text-txt-500 tlw-border-gray-500',
          'hover:tlw-text-txt-core hover:tlw-border-pr',
          'active:tlw-text-txt-core active:tlw-border-pr',
          'disabled:tlw-text-txt-400 disabled:tlw-border-gray-400',
        ],
      },
      {
        type: 'primary',
        outline: true,
        class: [
          'tlw-border tlw-shadow-none',
          'tlw-text-txt-core tlw-border-pr',
          'hover:tlw-text-txt-white hover:tlw-bg-pr',
          'active:tlw-text-txt-white active:tlw-bg-pr',
          'disabled:tlw-text-txt-400 disabled:tlw-border-gray-400',
        ],
      },
    ],
  }
);

export const IconButton = forwardRef<
  HTMLButtonElement & HTMLAnchorElement,
  IconButtonProps
>((props, ref) => {
  const {
    htmlType,
    icon,
    type = 'default',
    size = 'medium',
    iconSize,
    outline,
    square = false,
    className,
    href,
    children,
    ...restProps
  } = props;

  const cls = useMemo(() => {
    return tlwTwMerge(
      iconButtonVariants({ type, size, outline, square }),
      className
    );
  }, [type, size, outline, square, className]);

  const content = (
    <>
      {icon !== null && <Icon name={icon} size={iconSize} />}
      {children}
    </>
  );

  if (href) {
    return (
      <a
        ref={ref}
        className={cls}
        href={href}
        children={content}
        {...restProps}
      />
    );
  } else {
    return (
      <button
        ref={ref}
        className={cls}
        type={htmlType}
        children={content}
        {...restProps}
      />
    );
  }
});
